import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, Fuel, Tractor, Factory, Ship } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";

const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#0066FF] hover:underline">
    {children}
  </a>
);

const subs = [
  { icon: Fuel, title: "Gasolineras y estaciones de servicio independientes" },
  { icon: Fuel, title: "Distribuidoras de gasóleo de calefacción" },
  { icon: Fuel, title: "Flotas de transporte por carretera" },
  { icon: Tractor, title: "Sector agrícola: gasóleo agrícola (tipo B) con tipo reducido" },
  { icon: Factory, title: "Fabricantes que usan hidrocarburos como materia prima" },
  { icon: Factory, title: "Plantas de generación eléctrica con fueloil o gas natural" },
  { icon: Ship, title: "Empresas de transporte marítimo (bunker)" },
];

export default function SectorHidrocarburos() {
  return (
    <div className="App">
      <Navbar />
      <main className="bg-[#0A1628]">
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0E1F38] to-[#0A1628]" />
          <div className="absolute -top-20 right-1/3 w-[500px] h-[500px] rounded-full bg-[#FF6B00]/15 blur-[140px]" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="back-to-home">
              <ChevronLeft className="w-4 h-4" /> Volver al inicio
            </Link>
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B00] font-semibold">Sector</span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
              style={{ fontFamily: "Poppins" }}
            >
              Hidrocarburos
            </motion.h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-3xl">
              El impuesto especial sobre hidrocarburos puede representar entre el <strong className="text-white">30% y el 50%</strong> del precio final del producto. Diferir su pago hasta la puesta a consumo real supone una ventaja financiera considerable.
            </p>
          </div>
        </section>

        <section className="bg-[#F5F7FA] text-[#1A1A2E] py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily: "Poppins" }}>
              Subsectores que atendemos
            </h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {subs.map(({ icon: Icon, title }) => (
                <div key={title} className="flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#FF6B00] hover:-translate-y-1 transition-all">
                  <div className="w-11 h-11 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#FF6B00]" />
                  </div>
                  <span className="text-slate-700 text-sm leading-snug">{title}</span>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] text-white rounded-3xl p-8 md:p-12 max-w-4xl">
              <h3 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Poppins" }}>
                Beneficio fiscal: gasóleo agrícola
              </h3>
              <p className="mt-4 text-slate-300 leading-relaxed">
                El gasóleo agrícola tributa a tipo reducido para maquinaria
                agrícola. FiscalBond gestiona el acceso a este beneficio fiscal
                en coordinación con cooperativas agrícolas y explotaciones.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#0A1628] py-20 md:py-28">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "Poppins" }}>
              Normativa clave
            </h2>
            <ul className="mt-8 space-y-3 text-slate-300">
              <li>→ <Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Ley 38/1992, Título I, arts. 46-73</Ext></li>
              <li>→ <Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1998-23284">Ley 34/1998, del Sector de Hidrocarburos</Ext></li>
              <li>→ <Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32003L0096">Directiva 2003/96/CE (imposición energía)</Ext></li>
            </ul>

            <a
              href="/#contacto"
              data-testid="sector-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium px-7 py-4 rounded-xl transition-all shadow-xl shadow-[#0066FF]/30 hover:-translate-y-0.5"
            >
              Solicitar información
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
